/**
 * Order mutations used by the new-order wizard.
 *
 * All wrap RPCs (or, for file upload, Supabase Storage) so that the wizard
 * never bypasses RLS / state-machine guards.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';
import {
  ORDER_FILE_MIME,
  DEFAULT_MAX_BYTES,
  validateOrderFile,
  resolveOrderFileMime,
} from './orderFileTypes';
import { readEdgeErrorBody } from '../util/edge';

/**
 * Re-throw a `functions.invoke()` transport error with the Edge envelope's
 * REAL message instead of supabase-js's generic "Edge Function returned a
 * non-2xx status code". Without this, a meaningful 4xx like "Order has no
 * positive total to charge" (create-direct-billing-checkout's zero-total
 * guard) surfaced as pure noise - which is exactly how a routine data
 * condition got escalated as a suspected Stripe go-live outage.
 */
async function throwEdgeError(error: unknown, fallback: string): Promise<never> {
  const body = await readEdgeErrorBody(error);
  const message = body?.error?.message || body?.message;
  if (message) {
    const e = new Error(message) as Error & { code?: string };
    if (body?.error?.code) e.code = body.error.code;
    throw e;
  }
  if (error instanceof Error) throw error;
  throw new Error(fallback);
}

export interface CreateOrderInput {
  clinic_id: string;
  patient_id: string | null;
  delivery_address?: string | null;
  admin_observations?: string | null;
  /** User-facing order notes (non-custom orders). Stored on orders.notes. */
  notes?: string | null | undefined;
  /** Auto-computed delivery estimate (YYYY-MM-DD). Admins can edit later. */
  expected_delivery_date?: string | null | undefined;
  /**
   * When true, stamps orders.is_direct_billed = true so the order is treated
   * as billed-directly-to-the-patient (no clinic invoice, agent commission
   * does not accrue, production is gated on payment). Defaults to false.
   * Wired by the wizard's Direct Billing pseudo-clinic option (Task B).
   */
  is_direct_billed?: boolean | undefined;
  /**
   * Admin only: the clinic podiatrist this order is attributed to
   * (orders.podiatrist_id). create_order_with_lines honours it ONLY for an
   * admin caller (42501 for anyone else who passes it) and only when the id
   * is an ACTIVE clinic_members row of `clinic_id` whose user holds the
   * podiatrist role (23514 otherwise). Null / omitted keeps the historical
   * behaviour: the RPC stamps the caller. Wired by the wizard's admin
   * "Podiatrist for this order" picker.
   */
  podiatrist_id?: string | null | undefined;
  lines: Array<{
    line_type: string;
    /** Visual product family from the wizard's subtype picker (Phase 4b iter). */
    solution_subtype?:
      | 'carbon_insole'
      | 'pa12_insole'
      | 'tpu_insole'
      | 'eva_insole'
      | 'polypropylene_insole'
      | 'fibreglass_insole'
      | 'orthopaedic_sandal'
      | 'insoles_refurbishment'
      | undefined;
    product_id?: string | null | undefined;
    /**
     * Canonical catalog SKU for this line. Drives the agent-commission
     * bucket resolution (see commission_category() in the
     * 20261028000000_agent_commission_engine.sql migration). Resolved at
     * submit time via skuForVariant() for marketplace cart lines, or read
     * from the existing OTS selection. May be null for custom-made lines
     * where no catalog SKU exists; the trigger then accrues no commission.
     */
    catalog_sku?: string | null | undefined;
    /** Optional patient name attached to a ready-made / FITO line. */
    patient_name?: string | null | undefined;
    configuration?: Record<string, unknown> | undefined;
    modifications?:
      | Array<{
          region: 'rearfoot' | 'midfoot' | 'forefoot' | 'offloading';
          modification_type: string;
          intensity?: 'light' | 'medium' | 'strong' | undefined;
          parameters?: Record<string, unknown> | undefined;
        }>
      | undefined;
    quantity?: number | undefined;
    unit_price_aed?: number | undefined;
  }>;
}

export async function createOrderWithLines(
  client: SupabaseClient<Database>,
  input: CreateOrderInput,
): Promise<string> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'create_order_with_lines' as any,
    {
      p_clinic_id: input.clinic_id,
      p_patient_id: input.patient_id,
      p_lines: input.lines,
      p_delivery_address: input.delivery_address ?? null,
      p_admin_observations: input.admin_observations ?? null,
      p_notes: input.notes ?? null,
      p_expected_delivery_date: input.expected_delivery_date ?? null,
      p_is_direct_billed: input.is_direct_billed ?? false,
      // Always posted (as null when unset): PostgREST resolves the overload
      // from the posted keys, and null is the documented "stamp the caller".
      p_podiatrist_id: input.podiatrist_id ?? null,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  return data as unknown as string;
}

export async function submitOrder(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'submit_order' as any,
    { p_order_id: orderId } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/**
 * Stamp the wizard-computed completeness flag onto a DRAFT order. `draft_complete`
 * is a non-guarded orders column (not in guard_orders_admin_columns), so this is a
 * plain header UPDATE under the caller's existing RLS (admin or clinic member) —
 * the same path `updateDraftOrder` already uses for `patient_id`. The source of
 * truth for "complete" is the wizard's TypeScript validators; the DB never
 * re-derives it. The server gate (guard_draft_completeness + the
 * set_podiatrist_order_status raise) blocks the draft->submitted transition ONLY
 * when this is explicitly `false`; `null` (legacy) and `true` both pass.
 */
export async function setOrderDraftComplete(
  client: SupabaseClient<Database>,
  orderId: string,
  draftComplete: boolean,
): Promise<void> {
  const { error } = await client
    .from('orders')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update({ draft_complete: draftComplete } as any)
    .eq('id', orderId);
  if (error) throw error;
}

/**
 * Patches an existing draft order + its primary line so the wizard's "Save
 * as draft" button can re-persist progress without minting a new
 * order_number. RLS expects the caller to own the order (clinic match or
 * admin). Status is left untouched — the order stays in 'draft' until
 * `submitOrder` is called.
 */
export interface UpdateDraftOrderInput {
  patient_id?: string | null | undefined;
  delivery_address?: string | null | undefined;
  admin_observations?: string | null | undefined;
  /**
   * Item 7 wizard completeness flag (see setOrderDraftComplete). Passed here so
   * a draft re-save writes it in the SAME orders UPDATE as patient_id — atomic
   * with the header, instead of a separate call that could fail independently
   * and leave the flag stale. Non-guarded column; RLS is the same as patient_id.
   */
  draft_complete?: boolean | null | undefined;
  line: {
    line_type: string;
    solution_subtype?:
      | 'carbon_insole'
      | 'pa12_insole'
      | 'tpu_insole'
      | 'eva_insole'
      | 'polypropylene_insole'
      | 'fibreglass_insole'
      | 'orthopaedic_sandal'
      | 'insoles_refurbishment'
      | null
      | undefined;
    product_id?: string | null | undefined;
    /** Free-text patient name carried on the line when the order has no
     *  patient record (reorder-by-name / migrated-history model). */
    patient_name?: string | null | undefined;
    configuration?: Record<string, unknown> | undefined;
    modifications?:
      | Array<{
          region: 'rearfoot' | 'midfoot' | 'forefoot' | 'offloading';
          modification_type: string;
          intensity?: 'light' | 'medium' | 'strong' | undefined;
          parameters?: Record<string, unknown> | undefined;
        }>
      | undefined;
    quantity?: number | undefined;
    unit_price_aed?: number | undefined;
  };
}

export async function updateDraftOrder(
  client: SupabaseClient<Database>,
  orderId: string,
  input: UpdateDraftOrderInput,
): Promise<void> {
  const orderPatch: Record<string, unknown> = {};
  if (input.patient_id !== undefined) orderPatch.patient_id = input.patient_id;
  if (input.delivery_address !== undefined) orderPatch.delivery_address = input.delivery_address;
  if (input.admin_observations !== undefined) orderPatch.admin_observations = input.admin_observations;
  if (input.draft_complete !== undefined) orderPatch.draft_complete = input.draft_complete;
  if (Object.keys(orderPatch).length > 0) {
    const { error: orderErr } = await client
      .from('orders')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(orderPatch as any)
      .eq('id', orderId);
    if (orderErr) throw orderErr;
  }

  // Find the primary line (line_number = 1). Single-line-per-order is the
  // design contract — reorders mint a new order, not a new line.
  const { data: lineRows, error: lineErr } = await client
    .from('order_lines')
    .select('id')
    .eq('order_id', orderId)
    .order('line_number', { ascending: true })
    .limit(1);
  if (lineErr) throw lineErr;
  const primaryLineId = (lineRows ?? [])[0]?.id;
  if (!primaryLineId) {
    throw new Error('Draft order has no primary line to update.');
  }

  const linePatch: Record<string, unknown> = {
    line_type: input.line.line_type,
  };
  if (input.line.solution_subtype !== undefined) {
    linePatch.solution_subtype = input.line.solution_subtype;
  }
  if (input.line.product_id !== undefined) linePatch.product_id = input.line.product_id;
  // Keep the line's patient_id in lock-step with the header, mirroring
  // create_order_with_lines (which stamps line.patient_id = header p_patient_id).
  // Without this, switching a draft between a record-backed patient and a
  // record-less reorder leaves a STALE order_lines.patient_id that DB-side QR
  // minting + provider/podiatrist-of-record RLS still read.
  if (input.patient_id !== undefined) linePatch.patient_id = input.patient_id;
  // Always reconcile patient_name: a reorder draft carries the typed name on
  // the line (no patient record); a record-backed draft must clear any stale
  // name left over from a prior reorder selection. Empty -> NULL.
  linePatch.patient_name = (input.line.patient_name ?? '').trim() || null;
  if (input.line.configuration !== undefined) linePatch.configuration = input.line.configuration;
  if (input.line.modifications !== undefined) linePatch.modifications = input.line.modifications;
  if (input.line.quantity !== undefined) linePatch.quantity = input.line.quantity;
  if (input.line.unit_price_aed !== undefined) linePatch.unit_price_aed = input.line.unit_price_aed;

  const { error: updErr } = await client
    .from('order_lines')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update(linePatch as any)
    .eq('id', primaryLineId);
  if (updErr) throw updErr;

  // Re-sum the orders header when the patch touched a money-bearing field.
  // create_order_with_lines computed the header from the FIRST save's lines;
  // without this, changing quantity or price on a later draft re-save leaves
  // subtotal/tax/total stale. Shared re-sum with updateOrderLinePrice.
  if (input.line.quantity !== undefined || input.line.unit_price_aed !== undefined) {
    await resyncOrderTotals(client, orderId);
  }
}

/**
 * Patches a single order_line's prescription payload from the "Edit
 * prescription" dialog on OrderDetailPage. Unlike `updateDraftOrder`, this
 * targets a specific line id (not the primary line of a draft) and also
 * accepts an optional `expected_delivery_date` for the parent order so a
 * subtype change can re-promise the date in the same round-trip.
 *
 * RLS: podiatrists can update order_lines only while the order/line is
 * NOT in {delivered, cancelled, returned}; admins can update at any
 * status. Submitted orders fall under the podiatrist-allowed window, so
 * this call will succeed for the editable-statuses spec
 * (draft + submitted) without an RLS migration. Beyond submitted, the
 * UI hides the entry point, so RLS becomes a defence-in-depth check.
 */
/**
 * Bounds for editing order_lines.quantity from the prescription edit
 * dialog. ONE home for the ceiling: the dialog's stepper imports these.
 * The database has NO CHECK constraint on quantity (verified live
 * 2026-09-03), so until a migration adds one these bounds are the only
 * enforcement on the honest path; keep the SDK validating them so no
 * caller can write 0, a negative, or a decimal.
 */
/* -------------------------------------------------------------------------- */
/*  Recycle bin (migration 20270357)                                          */
/* -------------------------------------------------------------------------- */

/**
 * Move an order to the recycle bin. Replaces the old hard DELETE.
 *
 * Authorization is UNCHANGED by this batch: the RPC asks can_delete_order(),
 * which restates the predicate the two existing DELETE policies carry (admin
 * at any status, or the owning podiatrist while draft/submitted). A refusal
 * raises 42501 with a reason rather than silently affecting zero rows, which
 * is what the previous `.delete().select()` dance existed to detect.
 *
 * Idempotent: binning an order already in the bin keeps the original
 * deleted_at, so the 60 day countdown cannot be restarted by pressing twice.
 */
export async function softDeleteOrder(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'soft_delete_order' as any,
    { p_order_id: orderId } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/** Restore from the bin. Admin only; the order keeps its original number. */
export async function restoreOrder(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'restore_order' as any,
    { p_order_id: orderId } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/**
 * Empty the bin by hand. Admin only, and works from day one because a human
 * is pressing it (the AUTOMATIC purge is a different function, ships with no
 * cron schedule, and refuses while its app_settings flag is false).
 *
 * Pass null to empty everything, or ids to empty just those. The underlying
 * DELETE still fires the referenced-order guard, so an order tied to a
 * payment, a Zoho invoice or a reorder refuses and says so; and it now fires
 * the delete-audit trigger, so the row survives as JSONB in audit_log.
 * Returns how many rows were actually destroyed.
 */
export async function purgeDeletedOrdersNow(
  client: SupabaseClient<Database>,
  orderIds: string[] | null,
): Promise<number> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'purge_deleted_orders_now' as any,
    { p_order_ids: orderIds } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

export const ORDER_LINE_QUANTITY_MIN = 1;
export const ORDER_LINE_QUANTITY_MAX = 10;

/**
 * Thrown by updateOrderLineConfiguration when the line UPDATE succeeded
 * but the follow-up recompute_order_totals RPC failed: the line is
 * correct, the header subtotal/tax/total are STALE, and every surface
 * reading orders.total_aed (Stripe checkout mint, payment-mismatch
 * check, billing card) is wrong until a recompute succeeds. Callers must
 * surface this loudly and offer a retry; re-running the same save is a
 * safe retry (the line update is idempotent and the recompute re-sums
 * server-side).
 */
export class TotalsRecomputeFailedError extends Error {
  readonly orderId: string;
  constructor(orderId: string, cause: unknown) {
    super(
      `order ${orderId}: line saved but recompute_order_totals failed (${
        cause instanceof Error ? cause.message : String(cause)
      })`,
    );
    this.name = 'TotalsRecomputeFailedError';
    this.orderId = orderId;
  }
}

export interface UpdateOrderLineConfigurationInput {
  /** Line id (from order_lines.id). */
  orderLineId: string;
  /** Parent order id, for the optional delivery-date re-promise. */
  orderId: string;
  line_type: string;
  solution_subtype?:
    | 'carbon_insole'
    | 'pa12_insole'
    | 'tpu_insole'
    | 'eva_insole'
    | 'polypropylene_insole'
    | 'fibreglass_insole'
    | 'orthopaedic_sandal'
    | 'insoles_refurbishment'
    | null
    | undefined;
  product_id?: string | null | undefined;
  configuration?: Record<string, unknown> | undefined;
  modifications?:
    | Array<{
        region: 'rearfoot' | 'midfoot' | 'forefoot' | 'offloading';
        modification_type: string;
        intensity?: 'light' | 'medium' | 'strong' | undefined;
        parameters?: Record<string, unknown> | undefined;
      }>
    | undefined;
  /** Optional new expected delivery date (YYYY-MM-DD) on the parent order. */
  expected_delivery_date?: string | null | undefined;
  /**
   * Pairs on the line (order_lines.quantity). Pass ONLY when the user
   * actually changed it: providing it triggers a recompute_order_totals
   * round-trip after the line patch (line_total_aed is a generated
   * column, but the header totals have NO recompute trigger and would
   * otherwise go stale). Must be an integer within
   * [ORDER_LINE_QUANTITY_MIN, ORDER_LINE_QUANTITY_MAX].
   */
  quantity?: number | undefined;
}

export async function updateOrderLineConfiguration(
  client: SupabaseClient<Database>,
  input: UpdateOrderLineConfigurationInput,
): Promise<void> {
  const linePatch: Record<string, unknown> = {
    line_type: input.line_type,
  };
  if (input.solution_subtype !== undefined) {
    linePatch.solution_subtype = input.solution_subtype;
  }
  if (input.product_id !== undefined) linePatch.product_id = input.product_id;
  if (input.configuration !== undefined) linePatch.configuration = input.configuration;
  if (input.modifications !== undefined) linePatch.modifications = input.modifications;
  if (input.quantity !== undefined) {
    // Reject BEFORE any write: the DB itself has no CHECK on quantity, so
    // a 0 / negative / decimal here would land and silently corrupt the
    // generated line_total_aed.
    if (
      !Number.isInteger(input.quantity) ||
      input.quantity < ORDER_LINE_QUANTITY_MIN ||
      input.quantity > ORDER_LINE_QUANTITY_MAX
    ) {
      throw new Error(
        `quantity must be a whole number between ${ORDER_LINE_QUANTITY_MIN} and ${ORDER_LINE_QUANTITY_MAX}`,
      );
    }
    linePatch.quantity = input.quantity;
  }

  const { error: lineErr } = await client
    .from('order_lines')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update(linePatch as any)
    .eq('id', input.orderLineId);
  if (lineErr) throw lineErr;

  // Quantity multiplies money: line_total_aed regenerates by itself, but
  // orders.subtotal/tax/total do NOT (no trigger exists; verified live).
  // Recompute through the SAME sanctioned RPC the admin price edit and the
  // wizard draft re-save use, IMMEDIATELY after the line patch so a later
  // failure (e.g. the delivery-date write) can never leave money stale.
  // A failure here is distinguishable so the dialog can refuse to close
  // green over stale totals.
  if (input.quantity !== undefined) {
    try {
      await resyncOrderTotals(client, input.orderId);
    } catch (e) {
      throw new TotalsRecomputeFailedError(input.orderId, e);
    }
  }

  if (input.expected_delivery_date !== undefined) {
    const { error: orderErr } = await client
      .from('orders')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ expected_delivery_date: input.expected_delivery_date } as any)
      .eq('id', input.orderId);
    if (orderErr) throw orderErr;
  }
}

/* -------------------------------------------------------------------------- */
/*  Admin price override on an existing order line                            */
/* -------------------------------------------------------------------------- */

/**
 * UAE VAT placeholder, hard-coded at 5% to match the inline math in
 * `create_order_with_lines` (see
 * packages/db/supabase/migrations/20260606000000_phase4b_rpcs.sql:147-148).
 * Keep this constant in lockstep with the RPC; bumping the rate requires
 * editing both sites.
 */
export const TAX_RATE = 0.05;

/**
 * Re-sum an order's header totals from its lines' `line_total_aed`
 * (the generated `unit_price_aed * quantity` column), then PATCH
 * `orders.subtotal_aed / tax_aed / total_aed`. Shared by
 * `updateOrderLinePrice` (admin price edit) and `updateDraftOrder`
 * (draft re-save with a changed quantity/price) so the money math has
 * exactly one client-side home. See `updateOrderLinePrice`'s docstring
 * for why this is client-side rather than a DB trigger; the long-term
 * `recompute_order_totals(p_order_id)` SQL function remains the cleaner
 * destination for both callers.
 *
 * Throws on Supabase error and when the orders UPDATE matches zero rows
 * (RLS denial or a concurrently locked/deleted order) so callers never
 * report success over stale totals.
 */
async function resyncOrderTotals(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<void> {
  // Column-guard migration 20270203000000: orders.subtotal/tax/total are now
  // admin-only for direct writes; the ONE sanctioned path is the
  // recompute_order_totals definer RPC, which re-sums server-side from the
  // generated line_total_aed (a client can trigger a recompute but can no
  // longer write arbitrary totals). TAX_RATE math lives in the RPC now; the
  // exported constant remains for display-side estimates.
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'recompute_order_totals' as any,
    { p_order_id: orderId } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

export interface UpdateOrderLinePriceInput {
  orderId: string;
  orderLineId: string;
  /** New unit price in AED. Must be >= 0. */
  unit_price_aed: number;
}

/**
 * Admin-only: rewrite a single `order_lines.unit_price_aed` value and
 * recompute the parent `orders.subtotal_aed / tax_aed / total_aed`
 * header. Used by the order-detail page's pencil-icon "Edit price"
 * affordance so admins can correct a wrong cart price at any status,
 * including delivered / cancelled orders.
 *
 * Why client-side recompute (not a DB trigger):
 *   - `order_lines.line_total_aed` is a generated column
 *     (`generated always as (unit_price_aed * quantity) stored`),
 *     so it updates automatically when we PATCH unit_price_aed.
 *   - No DB trigger today recomputes the orders header from
 *     order_lines mutations - the RPCs (`create_order_with_lines`
 *     etc.) compute totals inline at create time. To avoid
 *     introducing a trigger with cascade-interaction risk against
 *     the existing `trg_recompute_order_status_on_line_update`
 *     guard (see migration 20261022000000_recompute_order_status_cascade_guard.sql)
 *     we re-sum from the SDK and PATCH the orders header in the
 *     same call.
 *   - Long-term, a `public.recompute_order_totals(p_order_id uuid)`
 *     SQL function would be cleaner; that's deliberately out of
 *     scope here.
 *
 * RLS expectations:
 *   - Admin UPDATE on `order_lines` at any status is allowed by
 *     migration 20261021000000_tighten_order_lines_update_rls.sql
 *     (is_admin() short-circuits USING + WITH CHECK).
 *   - Admin UPDATE on `orders` at any status is allowed by
 *     migration 20260601000700_rls.sql:256-271.
 *
 * Side effects NOT performed by this helper:
 *   - Agent commission triggers fire on orders.status transitions,
 *     not on line-price changes (see migration
 *     20261028000000_agent_commission_engine.sql:561,595). If you
 *     edit a submitted order's price, commissions stay computed on
 *     the OLD price. Out of scope for this admin override; track
 *     separately if it bites.
 *
 * Errors:
 *   - throws on Supabase error
 *   - throws when the line UPDATE returns zero rows (RLS denied)
 */
export async function updateOrderLinePrice(
  client: SupabaseClient<Database>,
  input: UpdateOrderLinePriceInput,
): Promise<void> {
  if (!Number.isFinite(input.unit_price_aed) || input.unit_price_aed < 0) {
    throw new Error('Unit price must be a non-negative number.');
  }

  // 1. Patch the line. The generated column line_total_aed auto-updates.
  const { data: updatedLine, error: lineErr } = await client
    .from('order_lines')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update({ unit_price_aed: input.unit_price_aed } as any)
    .eq('id', input.orderLineId)
    .select('id');
  if (lineErr) throw lineErr;
  if (!updatedLine || updatedLine.length === 0) {
    throw new Error(
      'Price update returned zero rows. RLS may have blocked the action; only admins can change line prices on an existing order.',
    );
  }

  // 2+3. Re-sum every line on the parent order and patch the header.
  // line_total_aed (generated unit_price * quantity) already reflects the
  // fresh value from step 1, so no arithmetic duplication on the client.
  await resyncOrderTotals(client, input.orderId);
}

export async function validateClinicStock(
  client: SupabaseClient<Database>,
  args: { clinicId: string; productId: string; size: string; quantity: number },
): Promise<boolean> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'validate_clinic_stock' as any,
    {
      p_clinic_id: args.clinicId,
      p_product_id: args.productId,
      p_size: args.size,
      p_quantity: args.quantity,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  return Boolean(data);
}

/* -------------------------------------------------------------------------- */
/*  File upload                                                               */
/* -------------------------------------------------------------------------- */

// Order-attachment type policy lives in ./orderFileTypes (single source of
// truth shared with the picker `accept`, the storage bucket allowlist, and the
// file list). ALLOWED_UPLOAD_MIME.scans mirrors ORDER_FILE_MIME so any external
// consumer stays correct after the 2026-07 document widening.
export const ALLOWED_UPLOAD_MIME: Record<string, string[]> = {
  scans: [...ORDER_FILE_MIME],
  prescriptions: ['application/pdf'],
};

export { DEFAULT_MAX_BYTES };

export interface UploadFileArgs {
  bucket: 'scans' | 'prescriptions';
  clinicId: string;
  orderId: string;
  orderLineId: string;
  file: File;
  fileKind: 'foot_scan' | 'photo' | 'prescription' | 'stl' | 'additional';
  uploaderId: string;
  /** Upload progress callback (0..1). */
  onProgress?: (fraction: number) => void;
  maxBytes?: number;
}

/** Path scheme per Phase 1 §08: clinic/{clinic}/orders/{order}/{line}/{file}. */
export function buildStoragePath(args: {
  clinicId: string;
  orderId: string;
  orderLineId: string;
  filename: string;
}): string {
  // Normalize the filename: strip path separators and unsafe chars.
  const safeName = args.filename
    .replace(/\.\./g, '_')
    .replace(/[\\/]/g, '_')
    .replace(/[^A-Za-z0-9._-]/g, '_');
  return `clinic/${args.clinicId}/orders/${args.orderId}/${args.orderLineId}/${Date.now()}_${safeName}`;
}

export interface UploadResult {
  fileId: string;
  storagePath: string;
}

/**
 * Uploads a file to the chosen Supabase Storage bucket and persists the
 * `order_line_files` row. Reports upload progress via `onProgress` if the
 * underlying transport supports it (we use the supabase-js v2 upload API,
 * which exposes byte-level progress via a promise wrapper).
 *
 * Validates type + size before issuing the upload.
 */
export async function uploadOrderFile(
  client: SupabaseClient<Database>,
  args: UploadFileArgs,
): Promise<UploadResult> {
  // Validate by extension + MIME (extension is the primary gate, which closes
  // the empty-content-type bypass). Rejections throw a clear message the caller
  // surfaces inline - never a silent drop.
  const check = validateOrderFile(args.file, args.maxBytes ?? DEFAULT_MAX_BYTES);
  if (!check.ok) throw new Error(check.reason);
  // Trustworthy content type: prefer a concrete browser type, else derive from
  // the extension, so PDFs/Excel are stored with a real MIME (the bucket
  // enforces it and the draft email + file list rely on it).
  const contentType = resolveOrderFileMime(args.file);

  const storagePath = buildStoragePath({
    clinicId: args.clinicId,
    orderId: args.orderId,
    orderLineId: args.orderLineId,
    filename: args.file.name,
  });

  // Supabase JS v2 upload — fire-and-await; progress callbacks via XHR are
  // not natively supported in supabase-js, but for the wizard UI we simulate
  // determinate progress: 0% -> 30% on start, 100% on resolve. This keeps the
  // UX honest without bringing in a custom XHR layer.
  args.onProgress?.(0.05);
  const uploadPromise = client.storage
    .from(args.bucket)
    .upload(storagePath, args.file, {
      cacheControl: '3600',
      upsert: false,
      contentType,
    });
  args.onProgress?.(0.3);
  const { error: upErr } = await uploadPromise;
  if (upErr) throw upErr;
  args.onProgress?.(0.9);

  // Insert the order_line_files row
  const insertPayload = {
    order_line_id: args.orderLineId,
    file_kind: args.fileKind,
    storage_bucket: args.bucket,
    storage_path: storagePath,
    file_name: args.file.name,
    file_size_bytes: args.file.size,
    mime_type: contentType,
    uploaded_by: args.uploaderId,
  };
  const { data, error } = await client
    .from('order_line_files')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .insert(insertPayload as any)
    .select('id, storage_path')
    .single();
  if (error) throw error;
  args.onProgress?.(1);
  return {
    fileId: (data as { id: string }).id,
    storagePath,
  };
}

/**
 * Soft-archive (or restore) an order document via the status-gated
 * archive_order_file RPC. Archiving sets is_archived=true plus the audit
 * columns and removes the file from the supplier Gmail draft (which only
 * attaches is_archived=false files). Authorization (admin any status; clinic
 * member draft/submitted) is enforced server-side inside the RPC.
 */
export async function archiveOrderFile(
  client: SupabaseClient<Database>,
  fileId: string,
  archived = true,
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'archive_order_file' as any,
    { p_file_id: fileId, p_archived: archived } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/**
 * Edit the patient name for an order via the status-gated
 * update_order_patient_name RPC. Always writes the order's own
 * order_lines.patient_name; additionally syncs the shared patients record ONLY
 * when it is this order's own record (same clinic, not shared) - otherwise the
 * record is left intact and `note` explains why. Authorization (admin any
 * status; clinic member draft/submitted) is enforced server-side inside the RPC.
 *
 * Returns `{ synced, note }`: `synced` is true when the canonical patients
 * record was also updated; `note` is a human-readable string when the shared/
 * foreign record was deliberately left unchanged (else null).
 */
export async function updateOrderPatientName(
  client: SupabaseClient<Database>,
  args: { orderId: string; lineId: string; name: string },
): Promise<{ synced: boolean; note: string | null }> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'update_order_patient_name' as any,
    { p_order_id: args.orderId, p_line_id: args.lineId, p_name: args.name } as unknown as Record<
      string,
      unknown
    >,
  );
  if (error) throw error;
  const d = (data ?? {}) as { synced?: boolean; note?: string | null };
  return { synced: Boolean(d.synced), note: d.note ?? null };
}

/**
 * Edit the patient gender for an order via the status-gated
 * update_order_patient_gender RPC. Same authorisation matrix as the name RPC
 * (admin any status; podiatrist of the order or clinic member while order +
 * line are draft/submitted; 42501 otherwise), enforced server-side. `gender`
 * must be one of male | female | other | prefer_not_to_say (22023 otherwise,
 * 22004 when empty).
 *
 * When the line's patient record exists AND is filed under the order's clinic
 * the RPC updates patients.gender, which every order of that patient reads
 * (intended), and returns `{ synced: true, note: null }`. When the line has no
 * patient record, or the record is filed under another clinic, it writes
 * configuration.gender on the line instead and returns `{ synced: false, note }`
 * with a human-readable explanation (or null).
 */
export async function updateOrderPatientGender(
  client: SupabaseClient<Database>,
  args: { orderId: string; lineId: string; gender: string },
): Promise<{ synced: boolean; note: string | null }> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'update_order_patient_gender' as any,
    {
      p_order_id: args.orderId,
      p_line_id: args.lineId,
      p_gender: args.gender,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  const d = (data ?? {}) as { synced?: boolean; note?: string | null };
  return { synced: Boolean(d.synced), note: d.note ?? null };
}

/* -------------------------------------------------------------------------- */
/*  Admin billing reassignment (Direct Billing <-> clinic)                    */
/* -------------------------------------------------------------------------- */

export interface ReassignBillingResult {
  status: 'ok' | 'needs_confirmation';
  reason?: string;
  conversion?: 'direct_to_clinic' | 'clinic_to_direct' | 'clinic_move';
  zoho_invoice_blocked?: boolean;
}

/**
 * Admin-only reassignment of an order between Direct Billing and a clinic (and
 * plain clinic-to-clinic). Enforced server-side by reassign_order_billing:
 * reorders are rejected; a direct-billed order the patient already PAID needs
 * `confirm: true` (typed confirmation) and returns with zoho_invoice_blocked=true
 * so the clinic is not double-billed; unpaid direct billing converts freely;
 * clinic->direct only when clean. Never touches order.status (no commission
 * re-accrual). Returns `{status:'needs_confirmation'}` when a typed confirmation
 * is required; throws (RPC raise) on a hard block.
 */
export async function reassignOrderBilling(
  client: SupabaseClient<Database>,
  args: { orderId: string; targetClinicId: string; toDirectBilled: boolean; confirm?: boolean },
): Promise<ReassignBillingResult> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'reassign_order_billing' as any,
    {
      p_order_id: args.orderId,
      p_target_clinic_id: args.targetClinicId,
      p_to_direct_billed: args.toDirectBilled,
      p_confirm: args.confirm ?? false,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  return (data ?? {}) as ReassignBillingResult;
}

/** Parsed jsonb returned by reassign_order_podiatrist. */
export interface ReassignOrderPodiatristResult {
  order_id: string;
  previous_podiatrist_id: string | null;
  podiatrist_id: string;
  /** False (and no audit row written) when the order already had this podiatrist. */
  changed: boolean;
}

/**
 * Admin-only reassignment of an order's podiatrist through the
 * reassign_order_podiatrist RPC, which replaces the tracker's former direct
 * `orders.podiatrist_id` UPDATE. Enforced server-side: the caller must be an
 * admin (42501), the order must exist (P0002), and the target must be an
 * ACTIVE clinic_members row of the ORDER's clinic whose user holds the
 * podiatrist role (23514). The RPC also writes the
 * 'order.podiatrist_reassigned' audit_log row; a no-op reassignment returns
 * `changed: false` and writes nothing. Throws the PostgREST error on any
 * raise so the caller can map the SQLSTATE to copy.
 */
export async function reassignOrderPodiatrist(
  client: SupabaseClient<Database>,
  args: { orderId: string; podiatristId: string },
): Promise<ReassignOrderPodiatristResult> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'reassign_order_podiatrist' as any,
    {
      p_order_id: args.orderId,
      p_podiatrist_id: args.podiatristId,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  const d = (data ?? {}) as Partial<ReassignOrderPodiatristResult>;
  return {
    order_id: String(d.order_id ?? args.orderId),
    previous_podiatrist_id: d.previous_podiatrist_id ?? null,
    podiatrist_id: String(d.podiatrist_id ?? args.podiatristId),
    changed: Boolean(d.changed),
  };
}

/**
 * Admin-only: clear or set an order's zoho_invoice_blocked flag. Surfaced ONLY
 * from the order detail page (a clinic member cannot clear a double-billing
 * guard - the RPC re-checks is_admin server-side).
 */
export async function overrideOrderZohoBlock(
  client: SupabaseClient<Database>,
  args: { orderId: string; blocked: boolean },
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'override_order_zoho_block' as any,
    { p_order_id: args.orderId, p_blocked: args.blocked } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/*  Direct billing - admin Mark-as-paid                                       */
/* -------------------------------------------------------------------------- */

/** Methods accepted by admin_mark_direct_billing_paid (migration 20270321000000). */
export const DIRECT_BILLING_PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'card_offline',
  'other',
] as const;
export type DirectBillingPaymentMethod =
  (typeof DIRECT_BILLING_PAYMENT_METHODS)[number];

/**
 * Admin-only: mark a direct-billed order as paid via the
 * `admin_mark_direct_billing_paid` RPC (migration 20270321000000, backlog
 * item 29). Unlike the bare column UPDATE this replaces, the RPC requires a
 * payment method, stamps a SERVER-side timestamp, records the acting admin,
 * sets client_payment_status='paid' and writes an audit_log row. The RPC
 * refuses non-direct-billed and already-paid orders with a P0001 whose
 * message is user-presentable.
 */
export async function adminMarkDirectBillingPaid(
  client: SupabaseClient<Database>,
  args: {
    orderId: string;
    method: DirectBillingPaymentMethod;
    note?: string | undefined;
  },
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'admin_mark_direct_billing_paid' as any,
    {
      p_order_id: args.orderId,
      p_method: args.method,
      p_note: args.note ?? null,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/*  Direct billing - Stripe checkout link                                     */
/* -------------------------------------------------------------------------- */

/**
 * Response shape of the create-direct-billing-checkout Edge Function.
 * Mirrors the function's jsonOk envelope payload.
 */
export interface CreateDirectBillingCheckoutResult {
  /** Hosted Stripe Checkout URL the patient pays at. */
  checkout_url: string;
  /** Stripe Checkout Session id (also persisted to orders.stripe_checkout_session_id). */
  session_id: string;
  /**
   * True when the stored session charged a DIFFERENT amount than the order's
   * current total (price/quantity/discount edited after the link was minted).
   * The old session is expired in Stripe and this link is a fresh one, so the
   * UI must tell the admin the previously-shared link no longer works.
   */
  regenerated?: boolean;
  /** The stale amount (AED) the cancelled link was charging. */
  previous_amount_aed?: number | null;
  /** False when Stripe refused to expire the stale session (cancel it manually). */
  previous_link_cancelled?: boolean | null;
}

/**
 * Mint (or reuse) a Stripe Checkout Session for a direct-billed order. Callable
 * by admins OR the order's author (the podiatrist). The Edge Function:
 *   - rejects orders that are not direct-billed or already paid (409)
 *   - rejects orders whose total_aed is <= 0 (422)
 *   - re-fetches the existing session via the Stripe API when
 *     orders.stripe_checkout_session_id is already set, so repeated clicks
 *     return a fresh URL for the same session (idempotent at the FE)
 *   - returns 503 when STRIPE_SECRET_KEY is not configured on the project
 *
 * On success it also persists session_id back onto
 * orders.stripe_checkout_session_id; the caller should invalidate the order
 * detail query so the DirectBillingPaymentCard flips its button copy from
 * "Send payment link" to "Copy payment link".
 */
/**
 * Item 30: expire a manually-paid order's still-live Stripe Checkout session
 * so the link sitting in the patient's inbox stops being payable. Called by
 * MarkPaidDialog right after admin_mark_direct_billing_paid succeeds. Admin
 * only; the Edge Function refuses unpaid orders and treats already-expired /
 * already-completed sessions as success (the goal - the link cannot collect -
 * is met either way).
 *
 * Returns { expired } - false with reason 'no_session' when the order never
 * had a single-order session. THROWS when Stripe refused, so the caller can
 * warn the admin that the link may still be live.
 */
export async function expireOrderCheckout(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<{ expired: boolean; reason?: string | null }> {
  const { data, error } = await client.functions.invoke<{
    data: { expired: boolean; reason?: string | null } | null;
    error: { code: string; message: string } | null;
  }>('create-direct-billing-checkout', {
    body: { expire_for_paid_order: orderId },
  });
  if (error) await throwEdgeError(error, 'Could not cancel the payment link.');
  if (!data) throw new Error('Empty response from create-direct-billing-checkout.');
  if (data.error) {
    throw new Error(data.error.message || 'Could not cancel the payment link.');
  }
  if (!data.data) {
    throw new Error('Malformed response from create-direct-billing-checkout.');
  }
  return data.data;
}

export async function createDirectBillingCheckout(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<CreateDirectBillingCheckoutResult> {
  const { data, error } = await client.functions.invoke<{
    data: CreateDirectBillingCheckoutResult | null;
    error: { code: string; message: string } | null;
  }>('create-direct-billing-checkout', { body: { order_id: orderId } });
  if (error) await throwEdgeError(error, 'Could not create payment link.');
  if (!data) throw new Error('Empty response from create-direct-billing-checkout.');
  if (data.error) {
    throw new Error(data.error.message || 'Could not create payment link.');
  }
  if (!data.data) {
    throw new Error('Malformed response from create-direct-billing-checkout.');
  }
  return data.data;
}

/* -------------------------------------------------------------------------- */
/*  Duplicate an existing order                                               */
/* -------------------------------------------------------------------------- */

/**
 * Clones an existing order into a fresh DRAFT belonging to the caller. Wraps
 * the `duplicate_order(p_source_order_id uuid)` RPC defined in
 * packages/db/supabase/migrations/20261114000100_duplicate_order_rpc.sql.
 *
 * The RPC:
 *   - allows admins OR the source order's podiatrist; everyone else gets a
 *     42501 raise.
 *   - mints a new order_number via generate_order_number(), sets
 *     podiatrist_id to the caller (not the source's author), resets status
 *     to 'draft' on both header and lines, and clears submitted_at,
 *     expected_delivery_date, direct_billing_paid_at, and the Stripe
 *     session ids.
 *   - copies clinic_id, patient_id, delivery_address, admin_observations,
 *     notes, is_direct_billed on the header, and every order_lines row
 *     (line_type, solution_subtype, production_path, product_id, patient_id,
 *     patient_name, configuration, modifications, unit_price_aed, quantity,
 *     catalog_sku).
 *   - recomputes subtotal_aed / tax_aed / total_aed from the cloned lines.
 *   - returns the new order id.
 *
 * The caller should invalidate the orders list query and then navigate the
 * user to the new draft's detail page.
 */
export async function duplicateOrder(
  client: SupabaseClient<Database>,
  sourceOrderId: string,
): Promise<string> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'duplicate_order' as any,
    { p_source_order_id: sourceOrderId } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  if (!data || typeof data !== 'string') {
    throw new Error('Empty response from duplicate_order.');
  }
  return data;
}

/* -------------------------------------------------------------------------- */
/*  Direct billing - payment groups (Combine Orders feature)                  */
/* -------------------------------------------------------------------------- */

/**
 * One row in the combinable-orders picker (modal table). Returned by
 * `listCombinableDirectBilledOrders`. Includes a derived `product_label` so
 * the picker does not need to repeat the configuration-blob heuristics that
 * the order tracker uses; for MVP we fall back to product.name_en or a
 * humanised line_type.
 */
export interface CombinableDirectBilledOrder {
  id: string;
  order_number: string;
  patient_name: string | null;
  created_at: string;
  product_label: string;
  total_aed: number;
  payment_group_id: string | null;
}

/**
 * One member-order row returned alongside a payment group. Includes the
 * minimum surface the CombinedGroupBanner needs to render its sibling list:
 * id + order_number for the link, total_aed for the right-aligned amount,
 * patient name for the row label, and direct_billing_paid_at so the banner
 * can dim out already-paid rows if a group payment lands but a per-order
 * fan-out has not finished.
 */
export interface PaymentGroupOrderMember {
  id: string;
  order_number: string;
  total_aed: number;
  direct_billing_paid_at: string | null;
  patient: { id: string; full_name: string } | null;
}

/**
 * Full payment-group payload returned by `getPaymentGroup`. Mirrors the
 * direct_billing_payment_groups row plus its joined member orders.
 */
export interface PaymentGroupDetail {
  id: string;
  total_aed: number;
  status: 'pending' | 'paid' | 'cancelled';
  paid_at: string | null;
  stripe_checkout_session_id: string | null;
  /**
   * Hosted Stripe Checkout URL persisted at session-mint time by the
   * create-direct-billing-checkout Edge Function (migration
   * 20261125000000_direct_billing_checkout_url). Null on legacy rows minted
   * before that migration; the UI falls back to a single Edge Function call
   * in that case so old groups still work.
   */
  stripe_checkout_url: string | null;
  orders: PaymentGroupOrderMember[];
}

/**
 * Response shape of the create-direct-billing-checkout Edge Function when
 * called in group mode. The function returns the new payment group's id
 * alongside the hosted Stripe Checkout URL so the caller can navigate or
 * cache without a follow-up round trip.
 */
export interface CombineDirectBillingOrdersResult {
  /** Payment group id (newly minted). */
  groupId: string;
  /** Hosted Stripe Checkout URL for the aggregate session. */
  checkoutUrl: string;
}

/**
 * Edge Function payload contract for the multi-order combine path. Sent
 * to create-direct-billing-checkout when `order_ids` is set (group mint)
 * or when `payment_group_id` is set (re-mint after a membership edit).
 */
interface CreateGroupCheckoutPayload {
  order_ids?: string[];
  payment_group_id?: string;
  /**
   * Admin discount override (AED, VAT-inclusive). Wire name MUST match the
   * Edge Function's CheckoutRequestBody.total_aed field. Renaming this drops
   * the override silently because the function does not read any other key.
   */
  total_aed?: number;
}

interface CreateGroupCheckoutEnvelope {
  data: {
    checkout_url: string;
    session_id: string;
    payment_group_id: string;
  } | null;
  error: { code: string; message: string } | null;
}

/**
 * Heuristic product label for the combine-orders picker. Mirrors the
 * formatProductLabel order of preference used in the order tracker but
 * scoped to the joined-rows shape returned by listCombinableDirectBilledOrders:
 * configuration.display_label -> configuration.product_name ->
 * product.name_en -> humanised line_type fallback. Custom-made lines
 * without a product join become "Custom insole" etc.
 */
function deriveCombinableProductLabel(line: {
  line_type: string | null;
  configuration: Record<string, unknown> | null;
  product: { name_en: string | null } | null;
}): string {
  const cfg = (line.configuration ?? {}) as Record<string, unknown>;
  const display = typeof cfg.display_label === 'string' ? cfg.display_label : null;
  if (display && display.trim()) return display.trim();
  const productName = typeof cfg.product_name === 'string' ? cfg.product_name : null;
  if (productName && productName.trim()) return productName.trim();
  if (line.product && typeof line.product.name_en === 'string' && line.product.name_en.trim()) {
    return line.product.name_en.trim();
  }
  if (line.line_type) {
    return line.line_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'Order';
}

/**
 * Admin-only: list every direct-billed order eligible to join a brand-new
 * combined payment group. Filters:
 *   - is_direct_billed = true
 *   - direct_billing_paid_at IS NULL
 *   - payment_group_id IS NULL  (orders already in a group are hidden)
 *
 * The optional `excludeOrderId` is a convenience for callers that want to
 * suppress a specific row client-side (e.g. avoid showing the "this order"
 * row twice when the dialog also injects it as a disabled-checked row).
 *
 * Returns rows sorted by created_at DESC so the most recent orders sit at
 * the top of the picker. Patient and primary-line joins are flattened to a
 * compact picker shape; the caller does not need to handle joined arrays.
 *
 * RLS: callers must be admin. Podiatrists do not see this table populated
 * because the consuming UI is admin-gated upstream.
 */
export async function listCombinableDirectBilledOrders(
  client: SupabaseClient<Database>,
  options: { excludeOrderId?: string | null } = {},
): Promise<CombinableDirectBilledOrder[]> {
  const select =
    'id, order_number, total_aed, created_at, payment_group_id,' +
    ' patient:patients(id, full_name),' +
    ' lines:order_lines(line_number, line_type, configuration,' +
    '   product:products(name_en))';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q: any = client
    .from('orders')
    .select(select)
    .eq('is_direct_billed', true)
    .is('direct_billing_paid_at', null)
    .is('payment_group_id', null)
    .order('created_at', { ascending: false });

  if (options.excludeOrderId) {
    q = q.neq('id', options.excludeOrderId);
  }

  const { data, error } = await q;
  if (error) throw error;

  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return rows.map((row) => {
    const lines = ((row.lines ?? []) as Array<Record<string, unknown>>)
      .slice()
      .sort((a, b) => Number(a.line_number ?? 0) - Number(b.line_number ?? 0));
    const primary = lines[0] ?? null;
    const patientObj = (row.patient ?? null) as { full_name?: string | null } | null;
    return {
      id: String(row.id),
      order_number: String(row.order_number ?? ''),
      patient_name: patientObj?.full_name ?? null,
      created_at: String(row.created_at ?? ''),
      total_aed: Number(row.total_aed ?? 0),
      payment_group_id: (row.payment_group_id ?? null) as string | null,
      product_label: primary
        ? deriveCombinableProductLabel({
            line_type: (primary.line_type ?? null) as string | null,
            configuration: (primary.configuration ?? null) as Record<string, unknown> | null,
            product: (primary.product ?? null) as { name_en: string | null } | null,
          })
        : 'Order',
    };
  });
}

/**
 * Admin-only: load a single payment group and its member orders. Used by
 * the CombinedGroupBanner on every member-order detail page so a single
 * round trip hydrates the sibling list, total, and Stripe session id.
 *
 * Throws when the row does not exist (or RLS hides it from the caller).
 */
export async function getPaymentGroup(
  client: SupabaseClient<Database>,
  groupId: string,
): Promise<PaymentGroupDetail> {
  const select =
    'id, total_aed, status, paid_at, stripe_checkout_session_id, stripe_checkout_url,' +
    ' orders:orders!orders_payment_group_id_fkey(' +
    '   id, order_number, total_aed, direct_billing_paid_at,' +
    '   patient:patients(id, full_name)' +
    ' )';

  const { data, error } = await client
    // The generated Database type does not yet include
    // direct_billing_payment_groups (the migration just landed and types are
    // regenerated separately). Cast at the boundary so the helper stays
    // typed for callers while we wait on pnpm db:gen-types to refresh.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('direct_billing_payment_groups' as any)
    .select(select)
    .eq('id', groupId)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error('Payment group not found (or RLS denied access).');
  }
  const row = data as unknown as Record<string, unknown>;

  const ordersRaw = (row.orders ?? []) as Array<Record<string, unknown>>;
  const orders: PaymentGroupOrderMember[] = ordersRaw.map((o) => ({
    id: String(o.id),
    order_number: String(o.order_number ?? ''),
    total_aed: Number(o.total_aed ?? 0),
    direct_billing_paid_at: (o.direct_billing_paid_at ?? null) as string | null,
    patient: (o.patient ?? null) as PaymentGroupOrderMember['patient'],
  }));

  const status = String(row.status ?? 'pending');
  const normalisedStatus: PaymentGroupDetail['status'] =
    status === 'paid' || status === 'cancelled' ? status : 'pending';

  return {
    id: String(row.id),
    total_aed: Number(row.total_aed ?? 0),
    status: normalisedStatus,
    paid_at: (row.paid_at ?? null) as string | null,
    stripe_checkout_session_id:
      (row.stripe_checkout_session_id ?? null) as string | null,
    stripe_checkout_url:
      (row.stripe_checkout_url ?? null) as string | null,
    orders,
  };
}

/**
 * Admin-only: mint a new direct-billing payment group from `orderIds` and
 * create the aggregate Stripe Checkout Session in one round trip. Delegates
 * to the create-direct-billing-checkout Edge Function, which:
 *   - validates every order is direct-billed and unpaid (409 otherwise)
 *   - inserts a direct_billing_payment_groups row (admin RLS)
 *   - sets orders.payment_group_id on every member order
 *   - creates the Stripe Session with aggregate line items, applying
 *     `totalAed` as the customer-facing total (supports admin discount
 *     overrides below the raw sum)
 *   - persists session id on the group row, NOT on individual orders;
 *     the webhook is responsible for fanning out direct_billing_paid_at,
 *     session id, and payment intent id onto every member order on
 *     checkout.session.completed
 *
 * The caller must invalidate every member order's detail query and the
 * combinable-orders picker query on success.
 */
export async function combineDirectBillingOrders(
  client: SupabaseClient<Database>,
  input: { orderIds: string[]; totalAed: number },
): Promise<CombineDirectBillingOrdersResult> {
  if (!Array.isArray(input.orderIds) || input.orderIds.length < 2) {
    throw new Error('At least two orders are required to create a combined payment group.');
  }
  if (!Number.isFinite(input.totalAed) || input.totalAed <= 0) {
    throw new Error('Total must be a positive AED amount.');
  }

  const body: CreateGroupCheckoutPayload = {
    order_ids: input.orderIds,
    total_aed: input.totalAed,
  };

  const { data, error } = await client.functions.invoke<CreateGroupCheckoutEnvelope>(
    'create-direct-billing-checkout',
    { body },
  );
  if (error) await throwEdgeError(error, 'Could not create combined payment link.');
  if (!data) throw new Error('Empty response from create-direct-billing-checkout.');
  if (data.error) {
    throw new Error(data.error.message || 'Could not create combined payment link.');
  }
  if (!data.data) {
    throw new Error('Malformed response from create-direct-billing-checkout.');
  }
  return {
    groupId: data.data.payment_group_id,
    checkoutUrl: data.data.checkout_url,
  };
}

/**
 * Admin-only: remove a single order from its current payment group. Sets
 * `orders.payment_group_id = NULL` for the row. If the group is left with
 * one or zero remaining members the group row is DELETEd, which (via the
 * orders.payment_group_id FK's ON DELETE SET NULL) cascades the last
 * remaining order back to the unaffiliated state in the same transaction.
 *
 * Refuses when the group has already settled (status = 'paid' OR
 * paid_at IS NOT NULL): the UI should hide the entry point before that
 * point, and this is a defence-in-depth guard.
 *
 * Errors:
 *   - throws on Supabase error
 *   - throws when zero rows were updated (RLS denied, or the order is not
 *     currently in a group)
 *   - throws when the group is already paid
 */
export async function uncombineOrderFromGroup(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<void> {
  // 1. Read the current group + sibling count so we know what to clean up.
  //    The generated Database type does not yet include
  //    orders.payment_group_id (the migration just landed). Cast at the
  //    boundary so the helper stays typed for callers while we wait on
  //    pnpm db:gen-types to refresh.
  const { data: orderRow, error: loadErr } = await client
    .from('orders')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .select('id, payment_group_id' as any)
    .eq('id', orderId)
    .maybeSingle();
  if (loadErr) throw loadErr;
  if (!orderRow) {
    throw new Error('Order not found (or RLS denied access).');
  }
  const groupId =
    (orderRow as unknown as { payment_group_id: string | null }).payment_group_id;
  if (!groupId) {
    throw new Error('Order is not currently part of a payment group.');
  }

  // 2. Guard: refuse if the group is already paid.
  const { data: groupRow, error: groupErr } = await client
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('direct_billing_payment_groups' as any)
    .select('id, status, paid_at')
    .eq('id', groupId)
    .maybeSingle();
  if (groupErr) throw groupErr;
  if (!groupRow) {
    throw new Error('Payment group not found (or RLS denied access).');
  }
  const groupRecord = groupRow as unknown as { status: string; paid_at: string | null };
  if (groupRecord.status === 'paid' || groupRecord.paid_at !== null) {
    throw new Error('Cannot remove an order from a payment group that has already been paid.');
  }

  // 3. Unlink the order from its group.
  const { data: updated, error: updateErr } = await client
    .from('orders')
    .update({
      payment_group_id: null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
    .eq('id', orderId)
    .select('id');
  if (updateErr) throw updateErr;
  if (!updated || updated.length === 0) {
    throw new Error(
      'Could not unassign order from its payment group (no row updated; RLS may have denied the write).',
    );
  }

  // 4. If the group now has <= 1 member, delete it. The orders FK is
  //    ON DELETE SET NULL, so the surviving order (if any) gets its
  //    payment_group_id cleared automatically by the cascade.
  const { count, error: countErr } = await client
    .from('orders')
    .select('id', { count: 'exact', head: true })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .eq('payment_group_id' as any, groupId);
  if (countErr) throw countErr;
  const remaining = count ?? 0;
  if (remaining <= 1) {
    const { error: delErr } = await client
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('direct_billing_payment_groups' as any)
      .delete()
      .eq('id', groupId);
    if (delErr) throw delErr;
  }
}

/**
 * Admin-only: regenerate the Stripe Checkout Session for an existing
 * payment group. Used by the CombinedGroupBanner's "Regenerate payment
 * link" action when an admin needs to apply a new discount or after a
 * membership edit invalidated the previous session.
 *
 * Delegates to the create-direct-billing-checkout Edge Function with
 * `{ order_ids, payment_group_id, total_aed? }`. The Edge Function's
 * top-level input gate requires EITHER order_id or order_ids on every
 * request, so the caller must supply the current member order ids (the
 * Edge Function then cross-checks they all share the same group). The
 * function cancels the old Stripe session (if any), mints a fresh one,
 * updates the group's total_aed when `newTotalAed` is provided, and
 * persists the new session id + URL back onto the group row.
 *
 * Why order ids? Without them the Edge Function returns 400
 * "order_id or order_ids is required" (the gate at index.ts:131-137 does
 * not look at payment_group_id). When the caller provides the order ids
 * AND payment_group_id, handleMultiOrder takes the explicit-regeneration
 * branch (index.ts:416-442), which is exactly the behaviour we want.
 *
 * The caller must invalidate every member order's detail query on success
 * so the banner re-renders with the new link.
 */
export async function regenerateGroupPaymentLink(
  client: SupabaseClient<Database>,
  groupId: string,
  orderIds: string[],
  newTotalAed?: number,
): Promise<{ checkoutUrl: string }> {
  if (newTotalAed !== undefined) {
    if (!Number.isFinite(newTotalAed) || newTotalAed <= 0) {
      throw new Error('New total must be a positive AED amount.');
    }
  }
  if (!Array.isArray(orderIds) || orderIds.length === 0) {
    throw new Error('At least one order id is required to regenerate the payment link.');
  }

  const body: CreateGroupCheckoutPayload = {
    order_ids: orderIds,
    payment_group_id: groupId,
  };
  if (newTotalAed !== undefined) {
    body.total_aed = newTotalAed;
  }

  const { data, error } = await client.functions.invoke<CreateGroupCheckoutEnvelope>(
    'create-direct-billing-checkout',
    { body },
  );
  if (error) await throwEdgeError(error, 'Could not regenerate payment link.');
  if (!data) throw new Error('Empty response from create-direct-billing-checkout.');
  if (data.error) {
    throw new Error(data.error.message || 'Could not regenerate payment link.');
  }
  if (!data.data) {
    throw new Error('Malformed response from create-direct-billing-checkout.');
  }
  return { checkoutUrl: data.data.checkout_url };
}
